import { call, put, takeLatest } from 'redux-saga/effects'

import {
  AxiosServerResponse,
  LOGIN_ROUTES,
  Server,
  ServerUNC,
  serverStructureFactory,
  SERVER_HEADERS,
} from 'src/services'
import { LOCAL_STORAGE_KEYS } from 'src/config'
import { showError, showServerError } from 'src/store/ducks/error'
import { addLoading, removeLoading } from 'src/store/ducks/loading'

import { AUTH_TYPES, AuthData, RequestAuthenticateAction } from './types'
import { setAuthenticated } from './actions'

type RequestData = Pick<
  UncDashboard.User & UncDashboard.Login,
  'usuario_emaillogin' | 'usuario_senha' | 'login_macadress'
>

export type DescobrirClienteResult = {
  cliente_codigo: string
  clienteregistro_hostws?: string
  clienteregistro_tiposistema?: string
}

export function* requestAuthenticateWatcher() {
  yield takeLatest(AUTH_TYPES.REQUEST_AUTHENTICATE, handleAuthentication)
}

export function* handleFetchCustomerId(email: string) {
  const { data }: AxiosServerResponse<DescobrirClienteResult> = yield call(
    ServerUNC.put,
    LOGIN_ROUTES.DESCOBRIR_CLIENTES_POR_EMAIL,
    { usuario_emaillogin: email },
    {
      headers: {
        [SERVER_HEADERS.PUBCLIENTEREGISTRO]: '',
      },
    },
  )

  if (data.isSuccessful && data.hasData) {
    const first =
      (typeof data.getFirstData === 'function' ? data.getFirstData() : null) ??
      (data as any).content?.DADOS?.[0]

    if (!first?.cliente_codigo) {
      yield put(showError(['Atenção! E-mail não encontrado.']))
      return undefined
    }

    Server.defaults.auth = {
      username: process.env.REACT_APP_API_USER || 'MASTER',
      password: process.env.REACT_APP_API_PASSWORD || 'MASTER',
    }

    if (
      process.env.NODE_ENV === 'production' &&
      first.clienteregistro_hostws
    ) {
      Server.defaults.baseURL = String(first.clienteregistro_hostws).replace(
        /\/+$/,
        '',
      )
    } else {
      Server.defaults.baseURL = process.env.REACT_APP_API_URL
    }

    yield call(
      [localStorage, localStorage.setItem],
      LOCAL_STORAGE_KEYS.CUSTOMER_REGISTRY,
      JSON.stringify(first),
    )

    const customer: DescobrirClienteResult = {
      ...first,
      cliente_codigo: String(first.cliente_codigo),
    }

    return customer
  }

  if (data.messages?.length) {
    yield put(showServerError(data.messages))
  } else {
    yield put(showError([]))
  }
}

export function* handleUserAuthRequest(
  { email, password, onAuthenticated }: RequestAuthenticateAction['payload'],
  customer: DescobrirClienteResult,
) {
  const requestData: RequestData = {
    usuario_emaillogin: email,
    usuario_senha: password,
    login_macadress: '-',
  }

  const { data }: AxiosServerResponse<AuthData> = yield call(
    Server.put,
    LOGIN_ROUTES.LOGIN_EMAIL,
    serverStructureFactory(requestData),
    {
      headers: {
        [SERVER_HEADERS.PUBCLIENTEREGISTRO]: customer.cliente_codigo,
        [SERVER_HEADERS.PUBTIPOSISTEMA]:
          customer.clienteregistro_tiposistema ?? '',
        [SERVER_HEADERS.PUBUSUARIOEMAIL]: email,
      },
    },
  )

  if (data.isSuccessful) {
    const authData = data.getFirstData()

    if (authData) {
      const authDataWithCustomerId: AuthData = {
        ...authData,
        cliente_codigo: customer.cliente_codigo,
        clienteregistro_tiposistema: customer.clienteregistro_tiposistema,
      }

      yield call(
        [localStorage, localStorage.setItem],
        LOCAL_STORAGE_KEYS.AUTH_DATA,
        JSON.stringify(authDataWithCustomerId),
      )

      yield put(setAuthenticated(authDataWithCustomerId))
      if (onAuthenticated) yield call(onAuthenticated)
    }
  } else if (data.messages.length) {
    yield put(showServerError(data.messages))
  } else {
    yield put(showError([]))
  }
}

export function* handleAuthentication(action: RequestAuthenticateAction) {
  try {
    yield put(addLoading(AUTH_TYPES.REQUEST_AUTHENTICATE))

    const customer: DescobrirClienteResult | undefined = yield call(
      handleFetchCustomerId,
      action.payload.email,
    )

    if (customer) {
      yield call(handleUserAuthRequest, action.payload, customer)
    }
  } catch (e: any) {
    if (e.message === 'Network Error') {
      const failedHost = e?.config?.baseURL || 'Servidor desconhecido'

      yield put(showError([`Erro de conexão (timeout)\n${failedHost}`]))
    } else if (e instanceof Error) {
      yield put(showError(e.message ? [e.message] : []))
    } else {
      yield put(showError(['An unknown error occurred.']))
    }
  } finally {
    yield put(removeLoading(AUTH_TYPES.REQUEST_AUTHENTICATE))
  }
}
